import { Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { IProblemaNfe } from '../../../shared/utils/validarNotaFiscal';

export interface DialogVerificarNfeProps {
  open: boolean;
  carregando: boolean;
  problemas: IProblemaNfe[];
  onClose: () => void;
  // Clique num problema de item específico (tem iditens) — quem chama abre
  // o item pra correção com essas mensagens marcadas no header (ver
  // DetalheDaNfe.tsx/handleAbrirItemComInconsistencia).
  onAbrirItem: (iditens: string, problemasDoItem: IProblemaNfe[]) => void;
  // Clique num problema que veio da comparação com o cadastro do produto (tem
  // idprodutos) — abre o cadastro completo do produto numa dialog, com a
  // inconsistência marcada no topo (ver DetalheDaNfe.tsx/handleAbrirCadastroProduto).
  onAbrirProduto: (idprodutos: string, problemasDoProduto: IProblemaNfe[]) => void;
}

// Dialog "burra" — só exibe o resultado que DetalheDaNfe.tsx já calculou
// chamando verificarNotaFiscalAntesDeEnviar (ver validarNotaFiscal.ts). Não
// participa da emissão em nada, só informa.
export const DialogVerificarNfe: React.FC<DialogVerificarNfeProps> = ({ open, carregando, problemas, onClose, onAbrirItem, onAbrirProduto }) => {
  const erros = problemas.filter((problema) => problema.severidade === 'erro');
  const avisos = problemas.filter((problema) => problema.severidade === 'aviso');

  // Agrupa por item (undefined = problema do cabeçalho da nota), mantendo a
  // ordem de aparição. Problemas do mesmo item sempre trazem o mesmo iditens
  // (ver validarNotaFiscal.ts), então dá pra abrir o item certo pra corrigir.
  const grupos = new Map<string, IProblemaNfe[]>();
  for (const problema of problemas) {
    const chave = problema.itemDescricao ?? '__cabecalho__';
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave)!.push(problema);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Verificação da Nota Fiscal
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {carregando ? (
          <Box display="flex" alignItems="center" gap={2} py={3}>
            <CircularProgress size={24} />
            <Typography>Verificando itens e cadastro dos produtos...</Typography>
          </Box>
        ) : problemas.length === 0 ? (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
            Nenhum problema encontrado nos itens ou nas configurações da nota.
          </Alert>
        ) : (
          <>
            <Box display="flex" gap={1} mb={2}>
              {erros.length > 0 && <Chip color="error" size="small" icon={<ErrorOutlineIcon />} label={`${erros.length} erro(s)`} />}
              {avisos.length > 0 && <Chip color="warning" size="small" icon={<WarningAmberIcon />} label={`${avisos.length} aviso(s)`} />}
            </Box>
            {Array.from(grupos.entries()).map(([chave, itens], indice) => (
              <Box key={chave} mb={indice < grupos.size - 1 ? 1.5 : 0}>
                <Typography variant="subtitle2" color="text.secondary">
                  {chave === '__cabecalho__' ? 'Dados gerais da nota' : chave}
                </Typography>
                <List dense disablePadding>
                  {itens.map((problema, idx) => {
                    const icone = problema.severidade === 'erro'
                      ? <ErrorOutlineIcon color="error" fontSize="small" />
                      : <WarningAmberIcon color="warning" fontSize="small" />;
                    const texto = (
                      <ListItemText
                        primary={`${problema.campo}: ${problema.mensagem}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    );

                    // Problema do cabeçalho da nota (sem iditens nem
                    // idprodutos) não tem pra onde levar o clique.
                    const temAcao = !!problema.iditens || !!problema.idprodutos;

                    return (
                      <ListItem key={idx} disableGutters sx={{ py: 0.25, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>{icone}</ListItemIcon>
                        <Box sx={{ flex: 1, minWidth: 0 }}>{texto}</Box>
                        {temAcao && (
                          <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                            {problema.iditens && (
                              <Tooltip title="Abrir item da nota">
                                <IconButton size="small" onClick={() => onAbrirItem(problema.iditens!, itens)}>
                                  <ReceiptLongIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {problema.idprodutos && (
                              <Tooltip title="Abrir cadastro do produto">
                                <IconButton
                                  size="small"
                                  // Só os problemas que são de fato do cadastro do produto (têm
                                  // idprodutos) — os de regra estática do item (CFOP, ICMS...) não
                                  // fazem sentido marcados no header do cadastro do produto.
                                  onClick={() => onAbrirProduto(problema.idprodutos!, itens.filter((p) => p.idprodutos))}
                                >
                                  <Inventory2Icon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
                {indice < grupos.size - 1 && <Divider sx={{ mt: 1 }} />}
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Chip
          variant="outlined"
          size="small"
          label="Essa verificação é um auxílio local — não substitui a validação oficial da SEFAZ"
        />
      </DialogActions>
    </Dialog>
  );
};
